import { Component, inject, OnInit, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormControl } from '@angular/forms';
import { combineLatest, firstValueFrom, Observable, of } from 'rxjs';
import { AtlasConfigService, AtlasDataService } from '../../../core/src';
import { catchError, map, startWith, switchMap } from 'rxjs/operators';
import { IAMService } from '@vendasta/iamv2';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';

interface Entitlement {
  id: string;
  name: string;
  description: string;
}

interface EntitlementState {
  error: boolean;
  loading: boolean;
  entitlement?: Entitlement;
}

interface PamDialogData {
  partnerId: string;
  accountGroupId?: string;
}

@Component({
  selector: 'atlas-pam-dialog',
  templateUrl: './pam-dialog.component.html',
  styleUrl: './pam-dialog.component.scss',
  standalone: false,
})
export class PamDialogComponent implements OnInit {
  private dialogRef = inject(MatDialogRef<PamDialogComponent>);
  private configService = inject(AtlasConfigService);
  private iamService = inject(IAMService);
  private snackbarService = inject(SnackbarService);
  private dataService = inject(AtlasDataService);

  public reason = new FormControl('');
  public entitlementState$: Observable<EntitlementState> = null;

  constructor(@Inject(MAT_DIALOG_DATA) public data: PamDialogData) {}

  ngOnInit(): void {
    this.entitlementState$ = this.configService.config$.pipe(
      switchMap((config) => this.iamService.getEntitlement(config.entitlementId)),
      map(
        (resp): EntitlementState => ({
          entitlement: {
            id: resp.entitlement.id,
            name: resp.entitlement.name,
            description: resp.entitlement.description,
          },
          loading: false,
          error: false,
        }),
      ),
      catchError(() =>
        of({
          loading: false,
          error: true,
        }),
      ),
      startWith({
        loading: true,
        error: false,
      }),
    );
  }

  async createGrant() {
    try {
      await firstValueFrom(
        combineLatest([this.dataService.userId$, this.configService.config$]).pipe(
          switchMap(([userId, cfg]) => {
            this.data.partnerId = this.data.partnerId || cfg.partnerId;
            this.data.accountGroupId = this.data.accountGroupId || cfg.accountId;
            return this.iamService.createPamGrant(
              userId,
              cfg.entitlementId,
              this.reason.value,
              this.data.partnerId,
              this.data.accountGroupId
            )}
          ),
        ),
      );
      this.dialogRef.close();
    } catch (error) {
      this.snackbarService.openErrorSnack('Unable to create privileged access grant');
    }
  }
}
